import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MessageCircle, ArrowRight } from "lucide-react";
import { webApi, errText } from "@/lib/webApi";

export function usePublicLinks() {
  const [links, setLinks] = useState({});
  useEffect(() => {
    webApi.get("/public").then((r) => setLinks(r.data)).catch(() => {});
  }, []);
  return links;
}

export function StartOnWhatsApp({ href }) {
  return (
    <p className="mt-8 text-sm text-slate-400" data-testid="start-on-whatsapp">
      New to Dicta?{" "}
      <a href={href} target="_blank" rel="noreferrer" data-testid="start-on-whatsapp-link" className="text-amber-300 underline-offset-4 hover:underline">
        Start on WhatsApp
      </a>
    </p>
  );
}

export function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#07090D] text-slate-200">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
        <Link to="/welcome" className="mb-10 font-display text-2xl font-bold tracking-tight text-slate-100" data-testid="web-brand">Dicta</Link>
        {children}
      </div>
    </div>
  );
}

export default function WebLogin() {
  const links = usePublicLinks();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    webApi.get("/me").then(() => nav("/app", { replace: true })).catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await webApi.post("/auth/request", { phone });
      setDone(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 className="font-display text-4xl font-bold text-slate-100 sm:text-5xl">Sign in</h1>
      <p className="mt-3 text-sm text-slate-400">We'll send a one-time link to your WhatsApp. No password needed.</p>
      {!done && (
        <form onSubmit={submit} className="mt-8 space-y-3" data-testid="web-login-form">
          <label htmlFor="wa-phone" className="text-xs uppercase tracking-wide text-slate-500">WhatsApp number</label>
          <input id="wa-phone" data-testid="web-login-phone-input" required inputMode="tel" placeholder="+91 98765 43210" value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-[#0E121A] px-4 py-3 text-base text-slate-100 outline-none transition-colors duration-200 focus:border-amber-300/60" />
          {error && <p className="text-sm text-red-400" data-testid="web-login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="web-login-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-md bg-amber-300 px-4 py-3 font-semibold text-[#07090D] transition-transform duration-150 hover:-translate-y-0.5 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Send link
          </button>
        </form>
      )}
      {done && (
        <div className="mt-8 rounded-lg border border-white/10 bg-[#0E121A] p-5" data-testid="web-login-sent">
          {!done.fallback_only && <p className="text-slate-200">{done.message}</p>}
          <p className="mt-3 text-sm text-slate-400">
            {done.fallback_only ? "Get your link straight from WhatsApp:" : "No message after a minute? WhatsApp only lets us message you first within 24 hours of your last message. Get the link straight from the chat:"}
          </p>
          <a href={links.wa_login} target="_blank" rel="noreferrer" data-testid="send-login-on-whatsapp-button"
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-amber-300/40 px-4 py-2 text-sm text-amber-200 transition-colors duration-200 hover:bg-amber-300/10">
            <MessageCircle className="h-4 w-4" /> Send LOGIN on WhatsApp
          </a>
          <button onClick={() => setDone(null)} data-testid="web-login-try-again" className="ml-3 text-sm text-slate-500 hover:text-slate-300">Use another number</button>
        </div>
      )}
      <StartOnWhatsApp href={links.wa_start} />
    </Shell>
  );
}
